/**
 * Adapters from API / view models to the generic `SalesPriceInput` consumed by
 * `salesPriceBreakdown` and `SalesPriceStrip`. Kept separate from
 * `salesPrice.ts` so the core math stays free of app-specific types.
 */
import type {
  BookingPricingLine,
  RateLineItem,
  RecommendationPricingSnapshot,
  RecommendationSnapshotLine,
} from "@/types/api";
import type { RateLineRow } from "@/types/rate";

import {
  surchargeDisplayOptionsFromRateLine,
  surchargesForLineDisplay,
} from "./surchargeDisplay";
import { salesPriceBreakdown, type SalesPriceInput } from "./salesPrice";

/** A booking's persisted pricing line (`GET /v1/bookings/:id/pricing`). */
export function salesInputFromBookingLine(
  line: BookingPricingLine,
  blOverride?: number | null,
): SalesPriceInput {
  return {
    frt: line.frt ?? 0,
    currency: line.currency,
    equipment_size: line.equipment_size,
    // The full surcharge list already includes per-B/L basis entries, so the
    // breakdown derives locals and per-B/L charges from it directly.
    surcharges: line.surcharges ?? [],
    quantity: line.quantity,
    blOverride: blOverride ?? undefined,
  };
}

/** A rate line from the detail table (`RateLineRow`). */
export function salesInputFromRateLineRow(line: RateLineRow): SalesPriceInput {
  return {
    frt: line.frt,
    currency: line.currency,
    equipment_size: line.equipment_size,
    surcharges: surchargesForLineDisplay(
      line.surcharges,
      surchargeDisplayOptionsFromRateLine(line),
    ),
    frt_pp: line.frt_pp,
    quantity: 1,
  };
}

/** A rate line from Rate Lookup search (`RateLineItem` / search result). */
export function salesInputFromRateLineItem(line: RateLineItem): SalesPriceInput {
  return {
    frt: line.frt,
    currency: line.currency,
    equipment_size: line.equipment_size,
    surcharges: surchargesForLineDisplay(line.surcharges, {
      surchargesOverlaid: line.surcharges_overlaid,
      noticeOverlays: line.notice_overlays,
    }),
    frt_pp: line.frt_pp,
    frt_pp_overlaid: line.frt_pp_overlaid,
    quantity: 1,
  };
}

/** One equipment line from a recommendation pricing snapshot. */
export function salesInputFromSnapshotLine(
  line: RecommendationSnapshotLine,
): SalesPriceInput {
  return {
    frt: line.frt,
    currency: line.currency,
    equipment_size: line.equipment_size,
    surcharges: line.surcharges ?? [],
    quantity: line.qty,
  };
}

/** Flatten every equipment line across a snapshot's contract groups. */
export function snapshotLines(
  snapshot?: RecommendationPricingSnapshot | null,
): RecommendationSnapshotLine[] {
  if (!snapshot?.breakdown) return [];
  return snapshot.breakdown.flatMap((contract) => contract.lines ?? []);
}

/**
 * Per-container breakdowns for each equipment line in a snapshot, tagged with a
 * compact equipment label — ready to feed `SalesPriceStrip`.
 */
export function snapshotStrips(snapshot?: RecommendationPricingSnapshot | null) {
  return snapshotLines(snapshot).map((line, i) => ({
    key: `${line.equipment_size ?? "?"}-${line.equipment_type ?? ""}-${i}`,
    label: equipmentLabel(line.equipment_size, line.equipment_type, line.qty),
    breakdown: salesPriceBreakdown(salesInputFromSnapshotLine(line)),
  }));
}

/** Compact equipment label, e.g. `40' HC × 2`. */
export function equipmentLabel(
  size?: string | null,
  type?: string | null,
  quantity = 1,
): string {
  const base = [size ? `${size}'` : null, type].filter(Boolean).join(" ") || "—";
  return quantity > 1 ? `${base} × ${quantity}` : base;
}
